import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PP_LANGUAGE, PP_REGION } from "../constants/storage";
import { Cookie } from "../services/storage";
import { ApplicationState } from "../store";
import {
  actionSetGeoIPOverride,
  actionSetPrivacyLanguageRegion,
} from "../store/actions/privacy-actions";
import { PrivacyState } from "../store/reducers/privacy-reducer";
import { UserState } from "../store/reducers/user-reducer";
import { UtilsLocale } from "../utils/UtilsLocale";
import { useCookie } from "./cookie-hooks";

export const usePrivacyPolicy = () => {
  const dispatch = useDispatch();
  const { isFCAllowed } = useCookie();

  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const privacyState = useSelector<ApplicationState, PrivacyState>(
    (state) => state.privacy
  );
  const { userCountryCode } = userState;
  const { isGeoIPOverride } = privacyState;

  useEffect(
    () => {
      // Override the current ppLanguage with GeoIP-derived language
      // if the user hasn't manually selected any language yet (isGeoIPOverride is true)
      if (userCountryCode !== "" && isGeoIPOverride) {
        const ppLangData = UtilsLocale.getPPLanguageForCountry(userCountryCode);
        if (ppLangData) {
          // Dispatch directly without saveCookie to avoid setting isGeoIPOverride to false
          dispatch(
            actionSetPrivacyLanguageRegion({
              language: `${ppLangData.languageCode}-${userCountryCode}`,
              region: ppLangData.region,
            })
          );
        }
      }
    },
    // eslint-disable-next-line
    [userCountryCode, isGeoIPOverride]
  );

  const updatePrivacyPolicyLanguage = (
    language: string,
    region: string,
    saveCookie = false
  ) => {
    if (saveCookie) {
      if (isFCAllowed) {
        Cookie.set(PP_REGION, region, { expires: 365 });
        Cookie.set(PP_LANGUAGE, language, { expires: 365 });
      }
      // Any deliberate language selection disables GeoIP override
      dispatch(actionSetGeoIPOverride({ isGeoIPOverride: false }));
    }

    dispatch(
      actionSetPrivacyLanguageRegion({
        language,
        region,
      })
    );
  };

  return {
    updatePrivacyPolicyLanguage,
    isGeoIPOverride,
  };
};
